import {
  isObservable,
  isBoxedObservable as isObservableValue,
  isObservableArray,
  isObservableMap,
  keys
} from 'mobx'
import { isStateTreeNode, getSnapshot } from '@invisr/mobx-state-tree'
import { RECORD_SYMBOL } from '../store/models/record/record'

const defaultOptions = {
  detectCycles: true,
  exportMapsAsObjects: true,
  recurseEverything: true,
  includeComputed: false,
  forWorker: false
}
function cache (map, key, value, options) {
  if (options.detectCycles) map.set(key, value)
  return value
}
function toJSHelper (source, options, __alreadySeen) {
  if (
    !options.recurseEverything &&
      !isObservable(source)
  ) {
    return source
  }
  if (typeof window !== 'undefined' && source instanceof Blob) return source
  if (typeof window !== 'undefined' && source instanceof File) return source 
  if (source instanceof ArrayBuffer) return source
  if (typeof source !== 'object') return source
  // Directly return null if source is null
  if (source === null) return null
  // Directly return the Date object itself if contained in the observable
  if (source instanceof Date) return source

  if (isStateTreeNode(source)) return getSnapshot(source)

  if (isObservableValue(source)) { return toJSHelper(source.get(), options, __alreadySeen) }
  // make sure we track the keys of the object
  if (isObservable(source)) keys(source)
  const detectCycles = options.detectCycles === true
  if (detectCycles && source !== null && __alreadySeen.has(source)) {
    return __alreadySeen.get(source)
  }
  if (isObservableArray(source) || Array.isArray(source)) {
    const res_1 = cache(__alreadySeen, source, [], options)
    const toAdd = source.map(function (value) {
      return toJSHelper(value, options, __alreadySeen)
    })
    res_1.length = toAdd.length
    for (let i = 0, l = toAdd.length; i < l; i++) res_1[i] = toAdd[i]
    return res_1
  }
  if (
    isObservableMap(source) ||
    Object.getPrototypeOf(source) === Map.prototype
  ) {
    if (options.exportMapsAsObjects === false) {
      const res_2 = cache(__alreadySeen, source, new Map(), options)
      source.forEach(function (value, key) {
        res_2.set(key, toJSHelper(value, options, __alreadySeen))
      })
      return res_2
    } else {
      const res_3 = cache(__alreadySeen, source, {}, options)
      source.forEach(function (value, key) {
        res_3[key] = toJSHelper(value, options, __alreadySeen)
      })
      return res_3
    }
  }

  // Fallback to the situation that source is an ObservableObject or a plain object
  const res = cache(__alreadySeen, source, {}, options)
  let objKeys
  if (source[RECORD_SYMBOL]) {
    objKeys = Object.getOwnPropertyNames(source)
  } else {
    objKeys = Object.keys(source)
  }
  for (const key of objKeys) {
    if (source[RECORD_SYMBOL] && key === '$') {
      res[key] = { dataSourceId: source[key].dataSourceId }
    } else {
      res[key] = toJSHelper(source[key], options, __alreadySeen)
    }
  }

  return res
}
export default function toJS (source, options) {
  // backward compatibility
  options = { ...defaultOptions, ...(options || {}) }
  options.detectCycles =
    options.detectCycles === undefined
      ? options.recurseEverything === true
      : options.detectCycles === true
  let __alreadySeen
  if (options.detectCycles) __alreadySeen = new Map()
  return toJSHelper(source, options, __alreadySeen)
}
if (typeof window !== 'undefined') window.toJS = toJS
